/**
 * Credentials of one credential profile (D-44): alias, channel, credential, priority, active flag.
 * Editable on the profile page; read-only on an app page, where a change would silently affect
 * every other app on the same profile.
 */
import type { CredentialProfileDetail, ProfileCredential } from "@aiyo/shared";
import { Key, Pencil, Plus, TrashBin } from "@gravity-ui/icons";
import { Alert, Button, Card, Switch, Table } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useLastDefined } from "../lib/hooks";
import {
  useCreateProfileEntry,
  useCredentials,
  useDeleteProfileEntry,
  useUpdateProfileEntry,
} from "../lib/queries";
import { ConfirmDialog, FormDialog } from "./dialogs";
import { SelectInput, TextInput } from "./fields";
import { ChannelChip, Code, EmptyBlock, InlineError, StateChip } from "./ui";

export function ProfileEntries({ profile, canEdit }: { profile: CredentialProfileDetail; canEdit: boolean }) {
  const { t } = useTranslation();
  const update = useUpdateProfileEntry(profile.id);
  const remove = useDeleteProfileEntry(profile.id);
  const [editing, setEditing] = useState<ProfileCredential | "new" | null>(null);
  const [toRemove, setToRemove] = useState<ProfileCredential | null>(null);
  const rows = profile.entries;

  const addButton = canEdit ? (
    <Button className="shrink-0" onPress={() => setEditing("new")}>
      <Plus />
      {t("assignments.add")}
    </Button>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      {rows.length > 0 && addButton ? <div className="flex justify-end">{addButton}</div> : null}
      {rows.length === 0 ? (
        <Card>
          <EmptyBlock
            icon={Key}
            title={t("assignments.empty")}
            hint={t("assignments.emptyHint")}
            action={addButton}
          />
        </Card>
      ) : (
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label={t("profiles.entries")} className="min-w-[720px]">
              <Table.Header>
                <Table.Column isRowHeader>{t("assignments.alias")}</Table.Column>
                <Table.Column>{t("assignments.channel")}</Table.Column>
                <Table.Column>{t("assignments.credential")}</Table.Column>
                <Table.Column>{t("assignments.priority")}</Table.Column>
                <Table.Column>{t("assignments.active")}</Table.Column>
                {canEdit ? <Table.Column className="text-end">{t("common.actions")}</Table.Column> : null}
              </Table.Header>
              <Table.Body>
                {rows.map((a) => {
                  const providerStatus = a.credential.provider.status;
                  return (
                    <Table.Row key={a.id} id={a.id}>
                      <Table.Cell>
                        <Code>{a.alias}</Code>
                      </Table.Cell>
                      <Table.Cell>
                        <ChannelChip channel={a.channel} />
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">{a.credential.name}</span>
                          <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
                            {a.credential.provider.name}
                            {providerStatus !== "active" ? (
                              <StateChip
                                ok={providerStatus === "degraded" ? "warn" : false}
                                label={t(`status.provider.${providerStatus}`)}
                              />
                            ) : null}
                            {a.credential.status === "disabled" ? (
                              <StateChip ok={false} label={t("status.credential.disabled")} />
                            ) : null}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="tabular-nums">{a.priority}</Table.Cell>
                      <Table.Cell>
                        <Switch
                          aria-label={t("assignments.active")}
                          isDisabled={!canEdit || update.isPending}
                          isSelected={a.is_active}
                          onChange={(v) => update.mutate({ id: a.id, is_active: v })}
                        >
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch>
                      </Table.Cell>
                      {canEdit ? (
                        <Table.Cell className="text-end">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              isIconOnly
                              aria-label={t("common.edit")}
                              onPress={() => setEditing(a)}
                            >
                              <Pencil />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              isIconOnly
                              aria-label={t("common.remove")}
                              onPress={() => setToRemove(a)}
                            >
                              <TrashBin />
                            </Button>
                          </div>
                        </Table.Cell>
                      ) : null}
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}
      <InlineError error={update.error} />

      {canEdit ? (
        <>
          <EntryDialog profileId={profile.id} target={editing} onClose={() => setEditing(null)} />
          <ConfirmDialog
            isOpen={toRemove !== null}
            onOpenChange={(open) => !open && setToRemove(null)}
            title={t("assignments.removeTitle")}
            body={t("assignments.removeBody")}
            confirmLabel={t("common.remove")}
            isPending={remove.isPending}
            error={remove.error}
            onConfirm={() => toRemove && remove.mutate(toRemove.id, { onSuccess: () => setToRemove(null) })}
          />
        </>
      ) : null}
    </div>
  );
}

function EntryDialog({
  profileId,
  target,
  onClose,
}: {
  profileId: string;
  target: ProfileCredential | "new" | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const credentials = useCredentials();
  const create = useCreateProfileEntry(profileId);
  const update = useUpdateProfileEntry(profileId);
  const shown = useLastDefined(target);
  const isNew = shown === "new";
  const existing = shown && shown !== "new" ? shown : null;

  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [alias, setAlias] = useState("");
  const [priority, setPriority] = useState("1");

  // biome-ignore lint/correctness/useExhaustiveDependencies: the form resets only when the dialog target changes.
  useEffect(() => {
    if (!target) return;
    create.reset();
    update.reset();
    setCredentialId(existing?.credential.id ?? null);
    setAlias(existing?.alias ?? "");
    setPriority(String(existing?.priority ?? 1));
  }, [target]);

  const all = credentials.data ?? [];
  // When editing, only credentials of the same channel can replace the one behind the alias (D-05).
  const options = all.filter((c) => !existing || c.provider.channel === existing.channel);
  const mutation = isNew ? create : update;

  return (
    <FormDialog
      isOpen={target !== null}
      onOpenChange={(open) => !open && onClose()}
      title={isNew ? t("assignments.add") : t("assignments.editTitle")}
      submitLabel={t("common.save")}
      isPending={mutation.isPending}
      isSubmitDisabled={!credentialId}
      error={mutation.error}
      onSubmit={() => {
        if (!credentialId) return;
        const body = { credential_id: credentialId, alias, priority: Number(priority) || 1 };
        if (existing) update.mutate({ id: existing.id, ...body }, { onSuccess: onClose });
        else create.mutate(body, { onSuccess: onClose });
      }}
    >
      {credentials.data && all.length === 0 ? (
        <Alert>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>
              {t("assignments.noCredentials")}{" "}
              <Link to="/credentials" className="font-medium text-accent underline">
                {t("assignments.goToCredentials")}
              </Link>
            </Alert.Description>
          </Alert.Content>
        </Alert>
      ) : null}
      <SelectInput
        label={t("assignments.credential")}
        placeholder={t("assignments.chooseCredential")}
        description={existing ? t("assignments.swapHint") : undefined}
        value={credentialId}
        onChange={setCredentialId}
        isRequired
        options={options.map((c) => ({
          id: c.id,
          label: c.name,
          hint: `${t(`channels.${c.provider.channel}`)} · ${c.provider.name}`,
        }))}
      />
      <TextInput
        name="alias"
        label={t("assignments.alias")}
        description={t("assignments.aliasHint")}
        placeholder="email-main"
        value={alias}
        onChange={(v) => setAlias(v.toLowerCase())}
        isRequired
        mono
      />
      <TextInput
        name="priority"
        type="number"
        label={t("assignments.priority")}
        description={t("assignments.priorityHint")}
        value={priority}
        onChange={setPriority}
        min={1}
        max={100}
        isRequired
      />
    </FormDialog>
  );
}
